import Utils from './utils';
import Constants, { AMP_TYPES } from './constants';

export const GET_CDP_NUMERIC_VALUE = (key, item, cdpParameters) => {
  if (
    !(
      key &&
      item &&
      key in item &&
      item[key] &&
      'value' in item[key] &&
      item[key].value &&
      item[key].value.length &&
      Utils.isNumeric(item[key].value[0])
    )
  )
    return Constants.DEFAULTS.CDP_PARAM;
  return {
    displayValue: item[key].displayValue,
    value: Number(item[key].value[0]),
    name: cdpParameters && key in cdpParameters ? cdpParameters[key] : '',
  };
};

export const GET_CDP_VALUE = (key, item, cdpParameters) => {
  if (!(key && item && key in item && item[key] && 'value' in item[key] && item[key].value && item[key].value.length))
    return Constants.DEFAULTS.CDP_PARAM;
  return {
    displayValue: item[key].displayValue,
    value: item[key].value,
    name: cdpParameters && key in cdpParameters ? cdpParameters[key] : '',
  };
};

export const GET_CDP_BOOLEAN_VALUE = (key, item, cdpParameters) => {
  if (!(key && item && key in item && item[key] && 'value' in item[key] && item[key].value && item[key].value.length))
    return Constants.DEFAULTS.CDP_PARAM;
  return {
    displayValue: item[key].displayValue,
    value: ['YES', 'Y'].includes(item[key].value[0].toUpperCase()),
    name: cdpParameters && key in cdpParameters ? cdpParameters[key] : '',
  };
};

export const IS_VALUE_FOUND_IN_CDP_VALUE_LIST = (key, item, value) => {
  const cdpParameter = GET_CDP_VALUE(key, item);
  if (!cdpParameter.value) return false;
  return cdpParameter.value.some((item) => item.toLowerCase() === value);
};

export const TEMPERATURE_RANGE = (rangeValue) => {
  let min = null,
    max = null;

  if (rangeValue) {
    const rangeArray = rangeValue.replace('°C', '').replace('to ', '').split(' ');
    if (rangeArray.length >= 2 && Utils.isNumeric(rangeArray[0]) && Utils.isNumeric(rangeArray[1])) {
      const expectedMin = Number(rangeArray[0]),
        expectedMax = Number(rangeArray[1]);
      min = Math.min(expectedMin, expectedMax);
      max = Math.max(expectedMin, expectedMax);
    }
  }

  return {
    min: min,
    max: max,
  };
};

export const OPEN_LOOP_GAIN = (aVo, idealGain, r1, r2, ampType = 'MISSING') => {
  if (ampType === 'MISSING') console.error('OPEN_LOOP_GAIN called with invalid params');
  const absIdeal = Math.abs(idealGain);
  switch (ampType) {
    case AMP_TYPES.INVERTING:
    case AMP_TYPES.NON_INVERTING:
      return 1e6 / (1 + Math.pow(10, aVo / 20) * (r1 / (r1 + r2)));
    case AMP_TYPES.BUFFER:
      return 1e6 / (1 + Math.pow(10, aVo / 20));
    default:
      return Math.abs((Math.pow(10, aVo / 20) / ((1 + Math.pow(10, aVo / 20)) / absIdeal) - absIdeal) / absIdeal) * 1e6;
  }
};

export const RESISTOR_TOLERANCE = (
  hasInternalResistors,
  resistorTolerance,
  resistorMatching,
  r1,
  r2,
  idealGain,
  isNonInverting,
) => {
  const absIdeal = Math.abs(idealGain);

  if (hasInternalResistors) {
    // r1 and r2 should be the same
    return (
      Math.abs(
        ((r1 * 1000 * (1 + resistorMatching / 100)) / (r1 * 1000) - // don't these cancel each other out?
          absIdeal) /
          absIdeal,
      ) * 1e6
    );
  } else {
    const addBy = isNonInverting && !hasInternalResistors ? 1 : 0; // extra value to add for non-inverting amplifiers
    const multiplyBy = isNonInverting && !hasInternalResistors ? 1 : -1;

    const result =
      Math.abs(
        (multiplyBy *
          ((r2 * 1000 * (1 + resistorTolerance / 100)) / (r1 * 1000 * (1 - resistorTolerance / 100)) +
            addBy -
            absIdeal)) /
          absIdeal,
      ) * 1e6;
    return result;
  }
};

export const RESISTOR_TEMPCO = (tempco, r1, r2, operatingTemperature, isNonInverting) => {
  let deltaRFn = (rValue) => (rValue * tempco * operatingTemperature) / 1e6,
    tempcoErrorR1,
    tempcoErrorR2;

  if (isNonInverting) {
    tempcoErrorR2 = Math.abs(((r2 + deltaRFn(r2)) / r1 + 1 - (r2 / r1 + 1)) / (r2 / r1 + 1));
    tempcoErrorR1 = Math.abs((r2 / (r1 + deltaRFn(r1)) + 1 - (r2 / r1 + 1)) / (r2 / r1 + 1));
  } else {
    tempcoErrorR2 = Math.abs(((r2 + deltaRFn(r2)) / r1 - r2 / r1) / (r2 / r1));
    tempcoErrorR1 = Math.abs((r2 / (r1 + deltaRFn(r1)) - r2 / r1) / (r2 / r1));
  }

  return Math.sqrt(Math.pow(tempcoErrorR1 * 1e6, 2) + Math.pow(tempcoErrorR2 * 1e6, 2));
};

export const CMRR_OFFSET = (inputVoltageFromParams, cmrrMin, offsetGain, outputVoltage) => {
  return (
    ((Math.abs(inputVoltageFromParams) / Math.pow(10, cmrrMin / 20)) * 1e6 * Math.abs(offsetGain)) /
    Math.abs(outputVoltage)
  );
};

export const PSRR_DC = (refPowerSupplyRipple, psrrDCMin, offsetGain, invertingAmpOutputVoltage) => {
  return (
    ((refPowerSupplyRipple / Math.pow(10, psrrDCMin / 20)) * 1e6 * Math.abs(offsetGain)) /
    Math.abs(invertingAmpOutputVoltage)
  );
};

export const INPUT_OFFSET_CURRENT = (inputOffsetCurrent, offsetGain, outputVoltage, r1, r2) => {
  return (((inputOffsetCurrent * ((r2 * r1) / (r1 + r2))) / 2) * 1e6 * Math.abs(offsetGain)) / Math.abs(outputVoltage);
};

export const INPUT_OFFSET_VOLTAGE = (vos, offsetGain, outputVoltage) => {
  return ((vos * Math.abs(offsetGain)) / Math.abs(outputVoltage)) * 1e6;
};

export const INPUT_OFFSET_VOLTAGE_DRIFT = (inputOffsetDrift, maxTemperatureRange, offsetGain, outputVoltage) => {
  return ((inputOffsetDrift * maxTemperatureRange * Math.abs(offsetGain)) / Math.abs(outputVoltage)) * 1e6;
};

export const BIAS_CURRENT = (biasInputCurrent, offsetGain, invertingAmpOutputVoltage, r1, r2) => {
  return (
    (biasInputCurrent * ((r2 * r1) / (r1 + r2)) * Math.abs(offsetGain) * 1e6) / Math.abs(invertingAmpOutputVoltage)
  );
};
